import { FmlxIcon, FmlxProgressBar } from 'fmlx-common-ui';
import React, { useEffect, useState } from 'react';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  NotificationProgressStatus,
  setNotificationProgressStatus,
  setNotification,
} from 'redux/reducers/notificationReducer';
import {
  selectProgress,
  selectProgressMessage,
  selectProgressStatus,
  selectShowProgress,
} from 'redux/selectors/StateSelectors';

interface ProgressBarProps {
  text?: string | null;
}

const ProgressBar = ({ text }: ProgressBarProps) => {
  const progress = useRMSelector(selectProgress);
  const message = useRMSelector(selectProgressMessage);
  const status = useRMSelector(selectProgressStatus);
  const showProgressBar = useRMSelector(selectShowProgress);
  const dispatch = useRMDispatch();

  const [isVisible, setIsVisible] = useState(showProgressBar);
  const [isMounted, setIsMounted] = useState(showProgressBar);
  const visibilityClass = isVisible ? 'opacity-100' : 'delay-[3s] opacity-0';

  useEffect(() => {
    if (status === NotificationProgressStatus.InProgress && progress >= 100) {
      dispatch(setNotificationProgressStatus(NotificationProgressStatus.Done));
    } else if (status !== NotificationProgressStatus.InProgress && progress < 100) {
      dispatch(setNotificationProgressStatus(NotificationProgressStatus.InProgress));
    }
  }, [progress, status]);

  useEffect(() => {
    if (showProgressBar) {
      if (!isVisible) {
        setIsVisible(true);
      }
      if (!isMounted) {
        setIsMounted(true);
      }
    } else {
      setIsVisible(false);
    }
  }, [showProgressBar]);

  const resetNotification = () => {
    dispatch(
      setNotification({
        progress: 0,
        message: null,
        progressStatus: NotificationProgressStatus.InProgress,
        showProgressBar: false,
      }),
    );
  };

  return (
    isMounted && (
      <div
        id={`progressbar-main-div`}
        data-testid={`progressbar-main-div`}
        className={`progressBar transition-opacity duration-1000 ${visibilityClass}`}
        onTransitionEnd={(e) => {
          if (e.propertyName === 'opacity' && !isVisible) {
            setIsMounted(false);
            resetNotification();
          }
        }}
      >
        <div className="flex flex-row gap-2">
          {status === NotificationProgressStatus.Error ||
          status === NotificationProgressStatus.Done ? (
            <FmlxIcon
              id={`progressbar-fmlxicon-${
                status === NotificationProgressStatus.Error ? 'error' : 'success'
              }`}
              data-testid={`progressbar-fmlxicon-${
                status === NotificationProgressStatus.Error ? 'error' : 'success'
              }`}
              name={status === NotificationProgressStatus.Error ? 'Cancel' : 'Default'}
              customSize={44}
              customColor={status === NotificationProgressStatus.Error ? 'Red' : 'Green'}
            />
          ) : (
            <FmlxProgressBar
              id={`progressbar-fmlxprogressbar`}
              data-testid={`progressbar-fmlxprogressbar`}
              type={FmlxProgressBar.Type.CIRCULAR}
              variant={
                progress < 0
                  ? FmlxProgressBar.Variant.INDETERMINATE
                  : FmlxProgressBar.Variant.DETERMINATE
              }
              size={FmlxProgressBar.Size.MEDIUM}
              value={Math.round(progress)}
              showLabel
            />
          )}
          <div
            id={`progressbar-message-text`}
            data-testid={`progressbar-message-text`}
            className="flex items-center"
          >
            {message ? message : text ? text : ''}
          </div>
        </div>
      </div>
    )
  );
};

export default ProgressBar;
